import { memo } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { FLEET_VEHICLE_STATUS_CONFIG } from "services/api/fleet/vehicles/consts";

// UI
import VehicleLocationFields from "ui/modules/fleet/vehicles/pages/components/VehicleLocationFields/VehicleLocationFields";
import { pickLocationErrors } from "ui/modules/fleet/vehicles/pages/components/VehicleLocationFields/errors";

import { getStatusField } from "./fields";
import { VEHICLE_STATUS_FIELDS_COMPONENTS } from "./consts";
import { VehicleStatusDetailFormProps } from "./types";

/**
 * Drugi korak promjene statusa — kontekst koji status traži (gdje je vozilo, do kada, zbog čega).
 *
 * Ovdje stoji samo raspored; polja specifična za status crta komponenta iz
 * `VEHICLE_STATUS_FIELDS_COMPONENTS`, po istoj podjeli po kojoj su na backendu razdvojeni
 * extenderi — dodavanje statusa je nova komponenta, a ne nova grana u ovoj datoteci.
 */
const VehicleStatusDetailForm: React.FC<VehicleStatusDetailFormProps> = (props) => {
  const { vehicleId, status, values, errors, onChange, onLocationChange } = props;

  const { t } = useTranslate();

  const StatusFields = VEHICLE_STATUS_FIELDS_COMPONENTS[status];
  const locationField = getStatusField(status, "location");
  const contactField = getStatusField(status, "contact");

  // Dok je lokacija one vrste koju status podrazumijeva, njezin nositelj je upravo kontakt
  // iz polja iznad (Servis, Dobavljač) — pa se ne pita dvaput. Druga vrsta lokacije znači
  // da vozilo nije ondje gdje status sugerira, i tada se nositelj bira ovdje.
  const hideLocationContact =
    !!contactField &&
    locationField?.locationDefault === "contact" &&
    values.location?.locationType === locationField.locationType;

  // Naslov je odabrani status — korak dolazi nakon izbora, pa podsjeća na što se odnosi.
  const statusLabel = t(FLEET_VEHICLE_STATUS_CONFIG[status]?.label ?? "");

  return (
    <div className="flex items-start justify-center px-4 pt-12 pb-6">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">
          {statusLabel}
        </h1>
        <p className="text-center text-md text-slate-500 mb-8">
          Dopunite podatke koje ovaj status traži.
        </p>

        <div className="border bg-white border-slate-200">
          {StatusFields && (
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                  Podaci statusa
                </h3>
                <p className="text-xs text-slate-500">
                  Označena polja traži odabrani status; ostala se mogu dopuniti kasnije
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <StatusFields
                  status={status}
                  vehicleId={vehicleId}
                  values={values}
                  errors={errors}
                  onChange={onChange}
                  show
                />
              </div>
            </div>
          )}

          {locationField && values.location && (
            <div className="py-6 px-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                  {locationField.label}
                </h3>
                <p className="text-xs text-slate-500">Gdje se vozilo od sada nalazi</p>
              </div>
              <VehicleLocationFields
                values={values.location}
                errors={pickLocationErrors(errors)}
                onChange={onLocationChange}
                layout="grid"
                showValidFrom={false}
                showNote={false}
                hideContact={hideLocationContact}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(VehicleStatusDetailForm);
